import { useStableMediaSrc } from "@/lib/hooks/shared/useStableMediaSrc";
import {
  CAPTION_BUBBLE_PADDING_PERCENT,
  CAPTION_EDGE_INSET_PERCENT,
  CAPTION_HORIZONTAL_INSET_PERCENT,
  CAPTION_LINE_HEIGHT_RATIO,
  DEFAULT_SUBTITLE_FONT_SCALE,
  DEFAULT_SUBTITLE_OFFSET,
  DEFAULT_SUBTITLE_POSITION,
  DEFAULT_SUBTITLE_PRESET,
  SUBTITLE_FONT_SCALE_MAX,
  SUBTITLE_FONT_SCALE_MIN,
  SUBTITLE_FONT_SIZE_REFERENCE_WIDTH,
  SUBTITLE_OFFSET_MAX,
  SUBTITLE_OFFSET_MIN,
  SUBTITLE_PRESETS,
  type SubtitlePosition,
  type SubtitlePreset,
  type SubtitleWord,
  buildSyllableSegments,
  clampSubtitleFontScale,
  clampSubtitleOffset,
  computeCues,
  findVisibleCue,
  getSubtitleFontSize,
  isSyllableCue,
} from "@ai-teach/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MdCheck,
  MdClose,
  MdDeleteOutline,
  MdEdit,
  MdErrorOutline,
  MdPause,
  MdPlayArrow,
  MdSubtitles,
} from "react-icons/md";
import { getSubtitleOverlayStyle } from "./subtitleOverlayStyle";

interface VideoPlayerProps {
  src: string;
  subtitles: SubtitleWord[];
  imageUrl: string | null;
  position?: string | null | undefined;
  preset?: string | null | undefined;
  fontScale?: number | null | undefined;
  offset?: number | null | undefined;
  onGenerate?: () => void;
  onGeneratePronunciation?: () => void;
  onEdit?: () => void;
  onClear?: () => void;
  onStyleChange?: (style: SubtitleStyleChange) => void;
  trimStart?: number | null | undefined;
  trimEnd?: number | null | undefined;
  onTrimChange?: (start: number | null, end: number | null) => void;
  isGenerating?: boolean;
  subtitleFailed?: boolean;
  // Rendered inside the player's own wide black frame (top-right of that frame, clear of
  // the narrow video itself) so it stays correctly placed regardless of the video's aspect.
  onClose?: () => void;
}

export interface SubtitleStyleChange {
  position: SubtitlePosition;
  preset: SubtitlePreset;
  fontScale: number;
  offset: number;
}

// Smallest allowed gap between the trim handles, in seconds.
const MIN_TRIM_GAP = 0.5;

const POSITIONS: SubtitlePosition[] = ["top", "center", "bottom"];

const FONT_SCALE_STEP = 0.1;
const OFFSET_STEP = 2;

function fmtTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1).padStart(4, "0");
  return `${m}:${sec}`;
}

export function VideoPlayer({
  src,
  subtitles,
  position: positionProp,
  preset: presetProp,
  fontScale: fontScaleProp,
  offset: offsetProp,
  onGenerate,
  onGeneratePronunciation,
  onEdit,
  onClear,
  onStyleChange,
  trimStart: trimStartProp,
  trimEnd: trimEndProp,
  onTrimChange,
  isGenerating,
  subtitleFailed,
  imageUrl,
  onClose,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scrubberRef = useRef<HTMLDivElement>(null);
  const trimBarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stableSrc = useStableMediaSrc(src);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  // The video's actual rendered box, computed in px once its real dimensions are known —
  // everything overlaid on the player (scrim, controls, captions) is anchored to this box
  // rather than to a container that only approximates the video's aspect ratio via CSS, so
  // nothing can end up positioned over letterboxed black space instead of the video itself.
  const [renderedSize, setRenderedSize] = useState<{ width: number; height: number } | null>(null);

  const computeRenderedSize = useCallback(() => {
    const v = videoRef.current;
    const container = containerRef.current;
    if (!v?.videoWidth || !v?.videoHeight || !container) return;
    const ratio = v.videoWidth / v.videoHeight;
    const availableWidth = container.clientWidth;
    const maxHeight = window.innerHeight * 0.6; // mirrors max-h-[60vh]
    const heightAtFullWidth = availableWidth / ratio;
    if (heightAtFullWidth <= maxHeight) {
      setRenderedSize({ width: availableWidth, height: heightAtFullWidth });
    } else {
      setRenderedSize({ width: maxHeight * ratio, height: maxHeight });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", computeRenderedSize);
    return () => window.removeEventListener("resize", computeRenderedSize);
  }, [computeRenderedSize]);

  const [trimStart, setTrimStart] = useState<number | null>(trimStartProp ?? null);
  const [trimEnd, setTrimEnd] = useState<number | null>(trimEndProp ?? null);
  const [dragHandle, setDragHandle] = useState<"start" | "end" | null>(null);

  // Position and preset are single discrete choices, so they commit on click and are read
  // straight from props.
  const position = (positionProp as SubtitlePosition) ?? DEFAULT_SUBTITLE_POSITION;
  const preset = (presetProp as SubtitlePreset) ?? DEFAULT_SUBTITLE_PRESET;

  const savedFontScale = clampSubtitleFontScale(fontScaleProp);
  const savedOffset = clampSubtitleOffset(offsetProp);

  // Size and nudge are dialled in over several clicks, so they are a draft applied on a
  // button — like trim below. Committing each step instead round-trips the whole slide
  // through the cache repeatedly while the author is still deciding.
  const [fontScale, setFontScale] = useState(savedFontScale);
  const [offset, setOffset] = useState(savedOffset);

  useEffect(() => {
    setFontScale(clampSubtitleFontScale(fontScaleProp));
  }, [fontScaleProp]);

  useEffect(() => {
    setOffset(clampSubtitleOffset(offsetProp));
  }, [offsetProp]);

  const isCaptionStyleDirty = fontScale !== savedFontScale || offset !== savedOffset;

  useEffect(() => {
    setTrimStart(trimStartProp ?? null);
  }, [trimStartProp]);

  useEffect(() => {
    setTrimEnd(trimEndProp ?? null);
  }, [trimEndProp]);

  // Effective playback bounds: a null trim means "no bound" → full clip edge.
  const effStart = trimStart ?? 0;
  const effEnd = trimEnd ?? duration;

  // Mirror the latest trim values so the mouseup handler can persist them
  // without re-subscribing the listeners on every drag frame.
  const trimRef = useRef<{ start: number | null; end: number | null }>({ start: null, end: null });
  trimRef.current = { start: trimStart, end: trimEnd };

  const seekTo = useCallback(
    (time: number) => {
      const v = videoRef.current;
      if (!v || duration === 0) return;
      v.currentTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(v.currentTime);
    },
    [duration]
  );

  const xToTime = useCallback(
    (clientX: number) => {
      const bar = scrubberRef.current;
      if (!bar || duration === 0) return 0;
      const rect = bar.getBoundingClientRect();
      return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * duration;
    },
    [duration]
  );

  useEffect(() => {
    if (!isScrubbing) return;
    const onMove = (e: MouseEvent) => seekTo(xToTime(e.clientX));
    const onUp = () => setIsScrubbing(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [isScrubbing, seekTo, xToTime]);

  // Per-frame playhead while playing — `timeupdate` (~4x/s) is too coarse for word highlighting.
  useEffect(() => {
    if (!isPlaying) return;
    let raf = 0;
    const tick = () => {
      const v = videoRef.current;
      if (v) {
        if (trimEnd != null && !v.paused && v.currentTime >= trimEnd) {
          v.pause();
          v.currentTime = effStart;
          setCurrentTime(effStart);
        } else {
          setCurrentTime(v.currentTime);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, trimEnd, effStart]);

  // Dragging a handle only updates local state (live preview); persistence
  // happens on "Apply" so we don't hammer the API on every drag.
  useEffect(() => {
    if (!dragHandle || duration === 0) return;
    const onMove = (e: MouseEvent) => {
      const bar = trimBarRef.current;
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      const t = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * duration;
      if (dragHandle === "start") {
        const clamped = Math.max(0, Math.min(t, (trimRef.current.end ?? duration) - MIN_TRIM_GAP));
        setTrimStart(clamped);
        seekTo(clamped); // frame-accurate feedback at the in-point
      } else {
        const clamped = Math.min(
          duration,
          Math.max(t, (trimRef.current.start ?? 0) + MIN_TRIM_GAP)
        );
        setTrimEnd(clamped);
        seekTo(clamped);
      }
    };
    const onUp = () => setDragHandle(null);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragHandle, duration, seekTo]);

  const handlePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      v.pause();
    } else {
      // Start inside the trim window if the playhead sits outside it.
      if (v.currentTime < effStart || v.currentTime >= effEnd - 0.05) {
        v.currentTime = effStart;
        setCurrentTime(effStart);
      }
      v.play();
    }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    // Non-destructive trim: stop playback at the trim end and rewind to the start.
    if (!v.paused && trimEnd != null && v.currentTime >= trimEnd) {
      v.pause();
      v.currentTime = effStart;
      setCurrentTime(effStart);
      return;
    }
    setCurrentTime(v.currentTime);
  };

  const hasSubtitles = subtitles.length > 0;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Trim editing is only available when the parent opts in via onTrimChange.
  const showTrim = !!onTrimChange && duration > 0;
  const startPct = duration > 0 ? (effStart / duration) * 100 : 0;
  const endPct = duration > 0 ? (effEnd / duration) * 100 : 100;
  const hasTrim = trimStart != null || trimEnd != null;

  // "Dirty" = local trim differs from the saved (prop) values → Apply is meaningful.
  const savedStart = trimStartProp ?? null;
  const savedEnd = trimEndProp ?? null;
  const isTrimDirty = trimStart !== savedStart || trimEnd !== savedEnd;

  const applyTrim = () => onTrimChange?.(trimStart, trimEnd);
  const clearTrim = () => {
    setTrimStart(null);
    setTrimEnd(null);
  };

  const cues = useMemo(() => computeCues(subtitles), [subtitles]);

  const activeCue = findVisibleCue(cues, currentTime);

  // Highlighting stays locked to the audio, so during the lead-in no word is lit yet.
  const activeWordIndexInCue = activeCue
    ? activeCue.findIndex((w) => currentTime >= w.start && currentTime <= w.end)
    : -1;
  const captionStyle = SUBTITLE_PRESETS[preset] ?? SUBTITLE_PRESETS[DEFAULT_SUBTITLE_PRESET];

  const fullWordFromCue0 = cues[0]?.[0]?.word ?? "";
  const syllableSegments =
    activeCue && isSyllableCue(activeCue, fullWordFromCue0)
      ? buildSyllableSegments(
          fullWordFromCue0,
          activeCue.map((w) => w.word)
        )
      : null;

  const overlayPositionStyle = getSubtitleOverlayStyle(
    position,
    offset,
    position === "top" ? CAPTION_EDGE_INSET_PERCENT.top : CAPTION_EDGE_INSET_PERCENT.bottom
  );

  const captionFontSize = getSubtitleFontSize(
    captionStyle.fontSize,
    fontScale,
    renderedSize?.width ?? SUBTITLE_FONT_SIZE_REFERENCE_WIDTH
  );

  const captionHorizontalInset =
    (renderedSize?.width ?? SUBTITLE_FONT_SIZE_REFERENCE_WIDTH) *
    (CAPTION_HORIZONTAL_INSET_PERCENT / 100);

  const captionBubbleHorizontalPadding =
    (renderedSize?.width ?? SUBTITLE_FONT_SIZE_REFERENCE_WIDTH) *
    (CAPTION_BUBBLE_PADDING_PERCENT / 100);

  // Immediate controls persist the *saved* size and nudge, never the pending draft — picking
  // a preset must not quietly commit an adjustment the author has not applied yet.
  const emitStyleChange = (next: Partial<SubtitleStyleChange>) =>
    onStyleChange?.({
      position,
      preset,
      fontScale: savedFontScale,
      offset: savedOffset,
      ...next,
    });

  const applyCaptionStyle = () => onStyleChange?.({ position, preset, fontScale, offset });

  // controls are always visible when paused or scrubbing; fade on hover while playing
  const controlsVisible = !isPlaying || isScrubbing;

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col w-full overflow-hidden shadow-2xl bg-black"
    >
      {/* Close button — lives in the wide frame, not on the (often much narrower) video
          itself, so it never overlaps the video content or its caption. */}
      {onClose && (
        <button
          type="button"
          aria-label="Close preview"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full text-white hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <MdClose className="w-5 h-5" />
        </button>
      )}

      {/* ── Video + overlaid controls ────────────────────────────────── */}
      <div
        className="relative group/player cursor-pointer select-none mx-auto"
        style={
          renderedSize ? { width: renderedSize.width, height: renderedSize.height } : undefined
        }
        onClick={handlePlayPause}
      >
        {/* biome-ignore lint/a11y/useMediaCaption: captions are the content being edited */}
        <video
          ref={videoRef}
          src={stableSrc}
          controls={false}
          className="w-full h-full block"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={() => {
            setDuration(videoRef.current?.duration ?? 0);
            computeRenderedSize();
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false);
            // snap to the end so the last word highlights (updates stop firing here)
            setCurrentTime(videoRef.current?.duration ?? duration);
          }}
        />

        {/* Big centered play button — only when paused */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-xl">
              <MdPlayArrow className="text-white text-4xl" />
            </div>
          </div>
        )}

        {/* Image */}
        {imageUrl && (
          <div className="absolute bottom-4 w-full flex justify-center">
            <img alt={imageUrl} src={imageUrl} className="h-[35%] w-[35%]" />
          </div>
        )}

        {/* Subtitle caption */}
        {activeCue && (
          <div
            className="absolute left-0 right-0 flex justify-center pointer-events-none"
            style={{
              ...overlayPositionStyle,
              paddingLeft: captionHorizontalInset,
              paddingRight: captionHorizontalInset,
            }}
          >
            <div
              style={{
                backgroundColor: captionStyle.backgroundColor,
                borderRadius: 6,
                padding: `5px ${captionBubbleHorizontalPadding}px`,
              }}
              className="text-center"
            >
              {syllableSegments ? (
                // Syllable cue — render as one continuous word, highlight per syllable
                <span
                  style={{
                    fontSize: captionFontSize,
                    lineHeight: `${captionFontSize * CAPTION_LINE_HEIGHT_RATIO}px`,
                    fontFamily: captionStyle.fontFamily,
                    fontWeight: captionStyle.fontWeight,
                    textShadow: captionStyle.textShadow
                      ? "0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)"
                      : undefined,
                    WebkitTextStroke: captionStyle.textOutline
                      ? `1.5px ${captionStyle.outlineColor}`
                      : undefined,
                  }}
                >
                  {syllableSegments.map((seg, i) => (
                    <span
                      key={`${i}-${seg.text}`}
                      style={{
                        color:
                          seg.sylIdx === activeWordIndexInCue
                            ? captionStyle.activeTextColor
                            : captionStyle.textColor,
                        transition: "color 0.1s",
                      }}
                    >
                      {seg.text}
                    </span>
                  ))}
                </span>
              ) : (
                // Normal cue — each word is its own spaced chip
                activeCue.map((w, i) => (
                  <span
                    key={`${w.start}-${w.word}`}
                    style={{
                      color:
                        i === activeWordIndexInCue
                          ? captionStyle.activeTextColor
                          : captionStyle.textColor,
                      fontSize: captionFontSize,
                      lineHeight: `${captionFontSize * CAPTION_LINE_HEIGHT_RATIO}px`,
                      fontFamily: captionStyle.fontFamily,
                      fontWeight: captionStyle.fontWeight,
                      textShadow: captionStyle.textShadow
                        ? "0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)"
                        : undefined,
                      WebkitTextStroke: captionStyle.textOutline
                        ? `1.5px ${captionStyle.outlineColor}`
                        : undefined,
                      transition: "color 0.1s",
                    }}
                  >
                    {i > 0 ? ` ${w.word}` : w.word}
                  </span>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Player controls overlay ──────────────────────────────── */}
        <div
          className={`absolute inset-x-0 bottom-0 transition-opacity duration-300 ${
            controlsVisible ? "opacity-100" : "opacity-0 group-hover/player:opacity-100"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Gradient scrim */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />

          <div className="relative px-4 pb-4 pt-10 space-y-2">
            {/* Scrubber */}
            <div
              ref={scrubberRef}
              className="group/scrubber relative h-4 flex items-center cursor-pointer"
              onMouseDown={(e) => {
                e.preventDefault();
                seekTo(xToTime(e.clientX));
                setIsScrubbing(true);
              }}
            >
              {/* Track */}
              <div className="absolute inset-x-0 h-[3px] group-hover/scrubber:h-[5px] rounded-full bg-white/25 overflow-visible transition-all duration-150">
                {/* Progress */}
                <div
                  className="absolute inset-y-0 left-0 bg-teal-400 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {/* Thumb */}
              <div
                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-lg transition-all duration-150 ${
                  isScrubbing
                    ? "opacity-100 scale-110"
                    : "opacity-0 group-hover/scrubber:opacity-100"
                }`}
                style={{ left: `${progress}%` }}
              />
            </div>

            {/* Controls row */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handlePlayPause}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors text-white"
              >
                {isPlaying ? <MdPause className="text-xl" /> : <MdPlayArrow className="text-xl" />}
              </button>

              <span className="text-xs font-mono tabular-nums text-white/60">
                <span className="text-white font-medium">{fmtTime(currentTime)}</span>
                {" / "}
                {fmtTime(duration)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ───────────────────────────────────────────────── */}
      <div className="self-stretch bg-gray-100 dark:bg-[#111] divide-y divide-gray-200 dark:divide-white/[0.06]">
        {/* Caption position + style row */}
        {onStyleChange && (
          <div className="px-4 py-2.5 flex items-center gap-4">
            {/* Position pills */}
            <div className="flex gap-1">
              {POSITIONS.map((pos) => (
                <button
                  key={pos}
                  type="button"
                  onClick={() => emitStyleChange({ position: pos })}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-full capitalize transition-all ${
                    position === pos
                      ? "bg-teal-500 text-white shadow-sm shadow-teal-500/30"
                      : "text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:text-white/40 dark:hover:text-white/70 dark:hover:bg-white/5"
                  }`}
                >
                  {pos}
                </button>
              ))}
            </div>

            <div className="w-px h-4 bg-gray-300 dark:bg-white/10 shrink-0" />

            {/* Preset swatches */}
            <div className="flex gap-1.5 items-center">
              {(Object.keys(SUBTITLE_PRESETS) as SubtitlePreset[]).map((key) => {
                const ps = SUBTITLE_PRESETS[key];
                const isActive = preset === key;
                return (
                  <button
                    key={key}
                    type="button"
                    title={ps.label}
                    onClick={() => emitStyleChange({ preset: key })}
                    style={{
                      backgroundColor:
                        ps.backgroundColor === "transparent"
                          ? "rgba(60,60,60,0.8)"
                          : ps.backgroundColor,
                      boxShadow: isActive
                        ? `0 0 0 2px ${ps.activeTextColor}`
                        : "0 0 0 2px transparent",
                    }}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold transition-all"
                  >
                    <span style={{ color: ps.activeTextColor }}>A</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Size and nudge get their own row: together with the presets they overflow the bar,
            which pushed Apply off screen. */}
        {onStyleChange && (
          <div className="px-4 py-2.5 flex items-center flex-wrap gap-x-4 gap-y-2">
            {/* Caption size — a multiplier of the preset's base size, so it carries to mobile */}
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-[11px] font-medium text-gray-400 dark:text-white/40">Size</span>
              <button
                type="button"
                title="Smaller captions"
                disabled={fontScale <= SUBTITLE_FONT_SCALE_MIN}
                onClick={() => setFontScale(clampSubtitleFontScale(fontScale - FONT_SCALE_STEP))}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:hover:text-white/70 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none transition-all"
              >
                −
              </button>
              <span className="text-[11px] font-mono tabular-nums w-9 text-center text-gray-600 dark:text-white/70">
                {fontScale.toFixed(1)}×
              </span>
              <button
                type="button"
                title="Larger captions"
                disabled={fontScale >= SUBTITLE_FONT_SCALE_MAX}
                onClick={() => setFontScale(clampSubtitleFontScale(fontScale + FONT_SCALE_STEP))}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:hover:text-white/70 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none transition-all"
              >
                +
              </button>
            </div>

            <div className="w-px h-4 bg-gray-300 dark:bg-white/10 shrink-0" />

            {/* Vertical nudge away from the chosen anchor, as a % of the player height */}
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-[11px] font-medium text-gray-400 dark:text-white/40">
                Nudge
              </span>
              <button
                type="button"
                title="Move captions up"
                disabled={offset <= SUBTITLE_OFFSET_MIN}
                onClick={() => setOffset(clampSubtitleOffset(offset - OFFSET_STEP))}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:hover:text-white/70 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none transition-all"
              >
                ↑
              </button>
              <span className="text-[11px] font-mono tabular-nums w-9 text-center text-gray-600 dark:text-white/70">
                {offset > 0 ? `+${offset}` : offset}%
              </span>
              <button
                type="button"
                title="Move captions down"
                disabled={offset >= SUBTITLE_OFFSET_MAX}
                onClick={() => setOffset(clampSubtitleOffset(offset + OFFSET_STEP))}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:hover:text-white/70 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none transition-all"
              >
                ↓
              </button>
              {(fontScale !== DEFAULT_SUBTITLE_FONT_SCALE ||
                offset !== DEFAULT_SUBTITLE_OFFSET) && (
                <button
                  type="button"
                  title="Reset size and nudge"
                  onClick={() => {
                    setFontScale(DEFAULT_SUBTITLE_FONT_SCALE);
                    setOffset(DEFAULT_SUBTITLE_OFFSET);
                  }}
                  className="ml-1 px-2 py-0.5 text-[11px] font-medium rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:text-white/40 dark:hover:text-white/70 dark:hover:bg-white/5 transition-all"
                >
                  Reset
                </button>
              )}
            </div>

            {/* Pushed to the end of the row so it stays put as the controls beside it change width */}
            <button
              type="button"
              onClick={applyCaptionStyle}
              disabled={!isCaptionStyleDirty}
              title={isCaptionStyleDirty ? "Save size and nudge" : "No changes to save"}
              className="ml-auto px-3 py-1 text-[11px] font-medium rounded-lg bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all shrink-0"
            >
              Apply
            </button>
          </div>
        )}

        {/* Trim row — a dedicated bar so it never conflicts with the seek scrubber */}
        {showTrim && (
          <div className="px-4 py-2.5 flex items-center gap-3">
            <span className="text-[11px] font-medium text-gray-400 dark:text-white/40 shrink-0">
              Trim
            </span>

            <div ref={trimBarRef} className="relative flex-1 h-6 flex items-center">
              {/* Base track */}
              <div className="absolute inset-x-0 h-1.5 rounded-full bg-gray-300 dark:bg-white/10" />
              {/* Selected region */}
              <div
                className="absolute h-1.5 rounded-full bg-amber-400/80"
                style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
              />
              {/* Playhead reference */}
              <div
                className="absolute w-0.5 h-3.5 bg-teal-500 rounded-full pointer-events-none"
                style={{ left: `${progress}%` }}
              />
              {/* Start handle */}
              <button
                type="button"
                aria-label="Trim start"
                title="Drag to set start"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDragHandle("start");
                }}
                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-6 rounded bg-amber-500 shadow cursor-ew-resize hover:bg-amber-400 transition-colors ${
                  dragHandle === "start" ? "ring-2 ring-amber-300" : ""
                }`}
                style={{ left: `${startPct}%` }}
              />
              {/* End handle */}
              <button
                type="button"
                aria-label="Trim end"
                title="Drag to set end"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDragHandle("end");
                }}
                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-6 rounded bg-amber-500 shadow cursor-ew-resize hover:bg-amber-400 transition-colors ${
                  dragHandle === "end" ? "ring-2 ring-amber-300" : ""
                }`}
                style={{ left: `${endPct}%` }}
              />
            </div>

            <span className="text-[11px] font-mono tabular-nums text-amber-600 dark:text-amber-300 shrink-0">
              {fmtTime(effStart)}–{fmtTime(effEnd)}
            </span>

            {hasTrim && (
              <button
                type="button"
                onClick={clearTrim}
                className="px-2 py-1 text-[11px] font-medium rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:text-white/40 dark:hover:text-white/70 dark:hover:bg-white/5 transition-all shrink-0"
              >
                Clear
              </button>
            )}

            <button
              type="button"
              onClick={applyTrim}
              disabled={!isTrimDirty}
              title={isTrimDirty ? "Save trim" : "No changes to save"}
              className="px-3 py-1 text-[11px] font-medium rounded-lg bg-amber-500 hover:bg-amber-400 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all shrink-0"
            >
              Apply
            </button>
          </div>
        )}

        {/* Actions row */}
        {(onGenerate || onEdit) && (
          <div className="px-4 py-3 flex items-center justify-between gap-3">
            {/* Status badge */}
            {subtitleFailed ? (
              <span className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 font-medium">
                <MdErrorOutline className="w-3.5 h-3.5" />
                Generation failed
              </span>
            ) : hasSubtitles ? (
              <span className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                <MdCheck className="w-3.5 h-3.5" />
                {subtitles.length} words
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-white/25">
                <MdSubtitles className="w-3.5 h-3.5" />
                No subtitles
              </span>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              {hasSubtitles && onClear && (
                <button
                  type="button"
                  onClick={onClear}
                  title="Remove subtitles"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 dark:border-white/10 dark:text-white/40 dark:hover:text-red-400 dark:hover:bg-red-500/10 dark:hover:border-red-500/30 transition-all"
                >
                  <MdDeleteOutline className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-200 hover:border-gray-300 dark:border-white/10 dark:text-white/60 dark:hover:text-white dark:hover:bg-white/5 dark:hover:border-white/20 transition-all"
                >
                  <MdEdit className="w-3.5 h-3.5" />
                  {hasSubtitles ? "Edit" : "Add manually"}
                </button>
              )}

              {onGeneratePronunciation && (
                <button
                  type="button"
                  onClick={onGeneratePronunciation}
                  disabled={isGenerating}
                  title="Generate using word syllable structure — recommended for pronunciation videos"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm shadow-violet-500/20"
                >
                  {isGenerating ? (
                    <>
                      <span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Generating…
                    </>
                  ) : (
                    "Pronunciation"
                  )}
                </button>
              )}

              {onGenerate && (
                <button
                  type="button"
                  onClick={onGenerate}
                  disabled={isGenerating}
                  title={onGeneratePronunciation ? "Generate using AI transcription" : undefined}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm shadow-teal-500/20"
                >
                  {isGenerating ? (
                    <>
                      <span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Generating…
                    </>
                  ) : hasSubtitles ? (
                    onGeneratePronunciation ? (
                      "Regenerate (AI)"
                    ) : (
                      "Regenerate"
                    )
                  ) : onGeneratePronunciation ? (
                    "AI Transcribe"
                  ) : (
                    "Generate Subtitles"
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
